// frontend/src/components/MyLink.js
// مكون إدارة الرابط الشخصي + الإحصائيات
import React, { useState, useEffect } from 'react';
import { Link2, Copy, Check, Share2, Send, Users, Eye, MessageSquare, Trophy, Star, TrendingUp, Info } from 'lucide-react';
import './MyLink.css';

const MyLink = ({ user }) => {
  const [linkInfo, setLinkInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const userId = user?.id || user?.telegramId;

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      try {
        const [linkRes, statsRes] = await Promise.all([
          fetch(`/api/link/${userId}/info`),
          fetch(`/api/link/${userId}/stats`)
        ]);

        if (linkRes.ok) {
          const data = await linkRes.json();
          setLinkInfo(data.link);
        }

        if (statsRes.ok) {
          const data = await statsRes.json();
          setStats(data.stats);
        }
      } catch (err) {
        console.error('Error fetching link data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  const handleCopy = () => {
    if (!linkInfo?.userLink) return;
    navigator.clipboard.writeText(linkInfo.userLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Fallback for browsers without clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = linkInfo.userLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShareTelegram = () => {
    if (!linkInfo?.userLink) return;
    const tg = window.Telegram?.WebApp;
    if (tg) {
      // استخدام Telegram Share API
      tg.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(linkInfo.userLink)}&text=${encodeURIComponent(linkInfo.shareText)}`);
    } else {
      window.open(`https://t.me/share/url?url=${encodeURIComponent(linkInfo.userLink)}&text=${encodeURIComponent(linkInfo.shareText)}`, '_blank');
    }
  };

  const handleShareGeneral = () => {
    if (!linkInfo?.userLink) return;
    if (navigator.share) {
      navigator.share({
        title: 'سرّك - محادثة مجهولة',
        text: linkInfo.shareText,
        url: linkInfo.userLink
      }).catch(() => {});
    } else {
      handleCopy();
    }
  };

  if (loading) {
    return (
      <div className="mylink-container">
        <div className="mylink-loading">
          <span className="loader-spinner"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="mylink-container">
      <h2><Link2 size={20} /> رابطي الشخصي</h2>

      {/* بطاقة الرابط */}
      <div className="mylink-card">
        <p className="mylink-card-title">
          <Eye size={14} />
          رابط المراسلة المجهولة
        </p>

        <div className="mylink-url-box">
          <div className="mylink-url-text">
            {linkInfo?.userLink || 'جاري التحميل...'}
          </div>
          <button
            className={`mylink-copy-btn ${copied ? 'copied' : ''}`}
            onClick={handleCopy}
          >
            {copied ? <><Check size={16} /> تم!</> : <><Copy size={16} /> نسخ</>}
          </button>
        </div>

        <div className="mylink-share-row">
          <button className="mylink-share-btn" onClick={handleShareTelegram}>
            <Send size={16} />
            مشاركة عبر تيليجرام
          </button>
          <button className="mylink-share-btn" onClick={handleShareGeneral}>
            <Share2 size={16} />
            مشاركة
          </button>
        </div>
      </div>

      {/* الإحصائيات */}
      {stats && (
        <>
          <div className="mylink-stats-grid">
            <div className="mylink-stat-card">
              <div className="mylink-stat-icon purple">
                <Eye size={18} />
              </div>
              <span className="mylink-stat-value">{stats.totalVisits || 0}</span>
              <span className="mylink-stat-label">إجمالي الزيارات</span>
            </div>

            <div className="mylink-stat-card">
              <div className="mylink-stat-icon blue">
                <Users size={18} />
              </div>
              <span className="mylink-stat-value">{stats.uniqueVisitors || 0}</span>
              <span className="mylink-stat-label">زوار فريدون</span>
            </div>

            <div className="mylink-stat-card">
              <div className="mylink-stat-icon green">
                <MessageSquare size={18} />
              </div>
              <span className="mylink-stat-value">{stats.activeChats || 0}</span>
              <span className="mylink-stat-label">محادثات نشطة</span>
            </div>

            <div className="mylink-stat-card">
              <div className="mylink-stat-icon gold">
                <Star size={18} />
              </div>
              <span className="mylink-stat-value">{stats.xpFromReferrals || 0}</span>
              <span className="mylink-stat-label">نقاط من الرابط</span>
            </div>
          </div>

          {/* الأداء التفصيلي */}
          <div className="mylink-performance-card">
            <h3><TrendingUp size={16} /> الأداء</h3>

            <div className="mylink-perf-row">
              <span className="mylink-perf-label">زيارات اليوم</span>
              <span className="mylink-perf-value">{stats.todayVisits || 0}</span>
            </div>

            <div className="mylink-perf-row">
              <span className="mylink-perf-label">آخر 7 أيام</span>
              <span className="mylink-perf-value">{stats.recentVisits || 0}</span>
            </div>

            <div className="mylink-perf-row">
              <span className="mylink-perf-label">عدد الإحالات</span>
              <span className="mylink-perf-value">{stats.referralCount || 0}</span>
            </div>

            <div className="mylink-perf-row">
              <span className="mylink-perf-label">إجمالي المحادثات</span>
              <span className="mylink-perf-value">{stats.totalChats || 0}</span>
            </div>

            <div className="mylink-perf-row">
              <span className="mylink-perf-label">المستوى الحالي</span>
              <span className="mylink-perf-value" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Trophy size={14} style={{ color: '#eab308' }} />
                {stats.currentLevel || 1}
              </span>
            </div>
          </div>
        </>
      )}

      {/* ملاحظة */}
      <div className="mylink-hint">
        <Info size={16} />
        <span>
          كل ما ينضم شخص جديد عبر رابطك تحصل على <strong>10 نقاط XP</strong> لرفع مستواك.
          شارك رابطك مع أصدقائك للحصول على نقاط أكثر! 🚀
        </span>
      </div>
    </div>
  );
};

export default MyLink;
